/**
 * Lazy video loader
 *
 * Targets `video.js-lazy-video` elements whose `<source>` tags carry the real
 * URL in `data-src`. No video bytes are downloaded at page load; sources are
 * attached and playback starts when the video approaches the viewport, and
 * playback pauses again when it leaves to save CPU/battery.
 */
(function () {
  /** Videos whose sources have already been attached */
  const loadedVideos = new WeakSet();

  /**
   * Attaches the real sources to a video (once) and starts playback.
   * @param {HTMLVideoElement} video - The lazy video element
   */
  const activateVideo = (video) => {
    if (!loadedVideos.has(video)) {
      for (const source of video.querySelectorAll('source[data-src]')) {
        const url = source.getAttribute('data-src');
        if (url) source.setAttribute('src', url);
      }
      video.load();
      loadedVideos.add(video);
    }

    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);

        if (entry.isIntersecting) {
          activateVideo(video);
        } else if (loadedVideos.has(video) && !video.paused) {
          video.pause();
        }
      }
    },
    // Start loading roughly two viewport-heights before the video is reached
    { rootMargin: '200% 0px' }
  );

  /**
   * Observes all lazy videos within a root that aren't observed yet.
   * @param {ParentNode} [root] - Scope to scan for lazy videos
   */
  const observeLazyVideos = (root = document) => {
    for (const video of root.querySelectorAll('video.js-lazy-video:not([data-lazy-observed])')) {
      video.setAttribute('data-lazy-observed', 'true');
      observer.observe(video);
    }
  };

  observeLazyVideos();

  // Re-scan when deferred sections inject their template content
  document.addEventListener('section:loaded', () => observeLazyVideos());
})();
